'use client';

import { FormEvent, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Button, Input } from 'rizzui';
import { LandingFaq } from '@/app/shared/landing/components/landing-faq';
import { homeFaqs } from '@/app/shared/landing/home/seo/home-seo-content';
import { HomeShipmentModal } from './home-shipment-modal';
import type {
  PublicShipment,
  PublicShipmentFreight,
  PublicShipmentLocation,
  PublicShipmentResponse,
  PublicShipmentTimelineItem,
  PublicShipmentTracking,
} from '@/app/shared/landing/tracking/public-shipment-types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNullableString(value: unknown) {
  return value === undefined || value === null || typeof value === 'string';
}

function normalizePublicShipment(
  raw: unknown,
  fallbackPro: string
): PublicShipment {
  if (!isRecord(raw)) {
    return {
      shipment: { pro: Number(fallbackPro) || fallbackPro },
      pickup: null,
      delivery: null,
    };
  }

  const rawShipment = isRecord(raw.shipment) ? raw.shipment : {};
  const proVal =
    rawShipment.pro ?? (raw as Record<string, unknown>).pro ?? fallbackPro;

  return {
    shipment: {
      pro: proVal as number | string,
      status:
        typeof rawShipment.status === 'string' ? rawShipment.status : null,
      mode: typeof rawShipment.mode === 'string' ? rawShipment.mode : null,
      description:
        typeof rawShipment.description === 'string'
          ? rawShipment.description
          : null,
      last_updated_at:
        rawShipment.last_updated_at !== undefined &&
        rawShipment.last_updated_at !== null
          ? String(rawShipment.last_updated_at)
          : null,
    },
    pickup: isRecord(raw.pickup)
      ? (raw.pickup as PublicShipmentLocation)
      : null,
    delivery: isRecord(raw.delivery)
      ? (raw.delivery as PublicShipmentLocation)
      : null,
    timeline: Array.isArray(raw.timeline)
      ? (raw.timeline as PublicShipmentTimelineItem[])
      : [],
    freight: isRecord(raw.freight)
      ? (raw.freight as PublicShipmentFreight)
      : null,
    tracking: isRecord(raw.tracking)
      ? (raw.tracking as PublicShipmentTracking)
      : null,
  };
}

export function HomeTrackingForm() {
  const [pro, setPro] = useState('');
  const [shipment, setShipment] = useState<PublicShipment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookupPro, setLookupPro] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const refreshInFlight = useRef(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const normalizedPro = pro.trim().replace(/^#/, '');
    if (!/^\d+$/.test(normalizedPro) || Number(normalizedPro) <= 0) {
      setLookupPro('');
      setRefreshError('');
      setShipment(null);
      setError('Please enter a valid PRO number.');
      setIsModalOpen(true);
      return;
    }

    setLookupPro(normalizedPro);
    setRefreshError('');
    setPro(normalizedPro);
    setShipment(null);
    setError('');
    setIsLoading(true);
    setIsModalOpen(true);

    await loadShipment(normalizedPro);
  }

  async function loadShipment(normalizedPro: string) {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/public/shipments/${normalizedPro}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const payload = (await response.json()) as PublicShipmentResponse;

      if (!response.ok || payload?.success === false || !payload?.data) {
        throw new Error(
          payload?.message ||
            'We could not find that shipment. Please check the PRO number.'
        );
      }

      setShipment(normalizePublicShipment(payload.data, normalizedPro));
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Shipment tracking is temporarily unavailable. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshTracking() {
    if (!lookupPro || refreshInFlight.current) return;
    refreshInFlight.current = true;
    setIsRefreshing(true);
    setRefreshError('');
    try {
      const response = await fetch(
        `/api/public/shipments/${lookupPro}/macropoint/refetch`,
        {
          method: 'POST',
          headers: { Accept: 'application/json' },
        }
      );
      const payload = (await response.json()) as { success?: boolean; message?: string };
      if (!response.ok || payload?.success === false) {
        throw new Error(
          payload?.message ||
            'Tracking could not be refreshed. Please try again.'
        );
      }
      await loadShipment(lookupPro);
    } catch (requestError) {
      setRefreshError(
        requestError instanceof Error
          ? requestError.message
          : 'Tracking could not be refreshed. Please try again.'
      );
    } finally {
      refreshInFlight.current = false;
      setIsRefreshing(false);
    }
  }

  return (
    <>
      <form
        className="ship-smart-hero__search at_fade_anim"
        data-fade-from="bottom"
        data-delay=".3"
        onSubmit={handleSubmit}
      >
        <Input
          type="search"
          name="load"
          inputMode="numeric"
          aria-label="Enter Load or PRO number"
          placeholder="Enter Load or PRO #"
          autoComplete="off"
          value={pro}
          onChange={(event) => setPro(event.target.value)}
          className="ship-smart-hero__input-shell"
          inputClassName="ship-smart-hero__search-input"
          required
        />
        <Button
          type="submit"
          variant="text"
          size="sm"
          disabled={isLoading || isRefreshing}
          className="ship-smart-hero__search-button !h-10 !min-h-0 !w-10 !rounded-full !p-0"
          aria-label="Track shipment"
          style={{
            width: 40,
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            padding: 0,
            borderRadius: '50%',
            background: 'var(--at-theme-primary, #03a1fe)',
            color: '#fff',
          }}
        >
          <Search
            aria-hidden="true"
            width={18}
            height={18}
            strokeWidth={2}
            style={{ color: '#fff' }}
          />
        </Button>
      </form>

      <HomeShipmentModal
        isOpen={isModalOpen}
        isLoading={isLoading}
        error={error}
        shipment={shipment}
        onRefresh={lookupPro ? refreshTracking : undefined}
        isRefreshing={isRefreshing}
        refreshError={refreshError}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

export function HomeFaq() {
  return (
    <section
      id="faq"
      className="sec-8-home-12"
      aria-label="Frequently asked questions"
    >
      <div className="sec-8-home-12__header">
        <span
          className="sec-8-home-12__eyebrow at_fade_anim"
          data-fade-from="bottom"
        >
          FAQ
        </span>
        <h2 className="sec-8-home-12__title reveal-text mb-0">
          Frequently Asked Questions
        </h2>
      </div>
      <LandingFaq
        className="sec-8-home-12__list"
        items={homeFaqs}
        variant="home"
      />
    </section>
  );
}
